import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  LinearProgress
} from '@mui/material';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import 'highlight.js/styles/github.css'; // Code highlighting styles
import { useCurrentUser } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';

interface Article {
  id: string;
  title: string;
  path: string;
  description: string;
  tags: string[];
  order: number;
}

interface Category {
  id: string;
  title: string;
  requiresPermission: string | null;
}

interface DocArticleProps {
  article: Article;
  category: Category;
  relatedArticles?: Array<{ category: string; article: Article }>;
}

export const DocArticle: React.FC<DocArticleProps> = ({
  article,
  category,
  relatedArticles = []
}) => {
  const [markdown, setMarkdown] = useState<string>('');
  const [displayedArticle, setDisplayedArticle] = useState(article);
  const [displayedCategory, setDisplayedCategory] = useState(category);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false);
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();

  // Permission check
  useEffect(() => {
    if (category.requiresPermission) {
      if (!user?.permissions?.includes(category.requiresPermission)) {
        navigate('/access-denied');
      }
    }
  }, [category.requiresPermission, user, navigate]);

  // Load markdown content
  useEffect(() => {
    const loadMarkdown = async () => {
      setLoading(true);
      setError(null);
      setFeedbackGiven(false); // Reset feedback when loading new article

      try {
        // Fetch markdown file from docs directory
        const response = await fetch(`/docs/${article.path}`);

        if (!response.ok) {
          throw new Error(`Failed to load article: ${response.statusText}`);
        }

        const text = await response.text();

        // Update all content at once to prevent flashing
        setMarkdown(text);
        setDisplayedArticle(article);
        setDisplayedCategory(category);
      } catch (err) {
        console.error('Error loading markdown:', err);
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadMarkdown();
  }, [article.path, article, category]);

  const handleFeedback = async (helpful: boolean) => {
    // TODO: Send feedback to analytics or API
    console.log('Feedback:', { article: displayedArticle.id, helpful });
    setFeedbackGiven(true);

    // Hide feedback buttons after 2 seconds
    setTimeout(() => {
      setFeedbackGiven(false);
    }, 2000);
  };

  const handleRelatedArticleClick = (relatedCategory: string, relatedArticle: Article) => {
    // Determine if it's a privileged article based on the category
    // This would need to be passed through or determined from metadata
    navigate(`/docs/${relatedCategory}/${relatedArticle.id}`);
  };

  // Only show loading spinner on initial load, not on subsequent navigations
  if (loading && isInitialLoad) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Article</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Loading indicator for subsequent navigations */}
      {loading && !isInitialLoad && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
      )}

      {/* Article Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
          {displayedArticle.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {displayedArticle.description}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {displayedArticle.tags.map(tag => (
            <Chip key={tag} label={tag} size="small" />
          ))}
        </Box>
      </Box>

      {/* Markdown Content */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          '& h2': {
            fontSize: '2rem',
            fontWeight: 600,
            mt: 4,
            mb: 2,
            color: 'text.primary'
          },
          '& h3': {
            fontSize: '1.5rem',
            fontWeight: 600,
            mt: 3,
            mb: 1.5,
            color: 'text.primary'
          },
          '& h4': {
            fontSize: '1.25rem',
            fontWeight: 600,
            mt: 2,
            mb: 1,
            color: 'text.primary'
          },
          '& p': {
            fontSize: '1rem',
            lineHeight: 1.7,
            mb: 2,
            color: 'text.primary'
          },
          '& ul, & ol': {
            mb: 2,
            pl: 3
          },
          '& li': {
            fontSize: '1rem',
            lineHeight: 1.7,
            mb: 0.5
          },
          '& pre': {
            backgroundColor: '#f6f8fa',
            borderRadius: 1,
            p: 2,
            overflow: 'auto',
            mb: 2
          },
          '& code': {
            backgroundColor: '#f6f8fa',
            borderRadius: 0.5,
            px: 0.5,
            py: 0.25,
            fontSize: '0.875em',
            fontFamily: 'monospace'
          },
          '& pre code': {
            backgroundColor: 'transparent',
            p: 0
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            pl: 2,
            ml: 0,
            my: 2,
            color: 'text.secondary',
            fontStyle: 'italic'
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2
          },
          '& th': {
            backgroundColor: '#f6f8fa',
            fontWeight: 600,
            p: 1.5,
            textAlign: 'left',
            borderBottom: '2px solid',
            borderColor: 'divider'
          },
          '& td': {
            p: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            my: 2
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeHighlight,
            rehypeSlug,
            [rehypeAutolinkHeadings, { behavior: 'wrap' }]
          ]}
        >
          {markdown}
        </ReactMarkdown>
      </Paper>

      {/* Feedback Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, backgroundColor: '#f9fafb' }}>
        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
          Was this article helpful?
        </Typography>
        {feedbackGiven ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Thank you for your feedback!
          </Alert>
        ) : (
          <Box display="flex" gap={2} mt={2}>
            <Button
              variant="outlined"
              startIcon={<ThumbUp />}
              onClick={() => handleFeedback(true)}
            >
              Yes
            </Button>
            <Button
              variant="outlined"
              startIcon={<ThumbDown />}
              onClick={() => handleFeedback(false)}
            >
              No
            </Button>
          </Box>
        )}
      </Paper>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Related Articles
            </Typography>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              {relatedArticles.map(({ category: relCategory, article: relArticle }) => (
                <Paper
                  key={relArticle.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      elevation: 3,
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => handleRelatedArticleClick(relCategory, relArticle)}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    {relArticle.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {relArticle.description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
